import { CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';

export interface CountryData {
  code: CountryCode;
  name: string;
  dialCode: string;
  flag: string;
  searchTerms: string;
}

/**
 * Generate flag emoji from ISO country code using Unicode regional indicator symbols
 * Each letter is offset by 127397 to convert A-Z to the regional indicator range
 */
const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * Get country name in French using the native Intl.DisplayNames API
 * This provides accurate translations for all countries and territories
 */
const getCountryName = (code: CountryCode): string => {
  try {
    const displayNames = new Intl.DisplayNames(['fr'], { type: 'region' });
    return displayNames.of(code) || code;
  } catch {
    return code;
  }
};

/**
 * Normalize text for search by removing accents and converting to lowercase
 * Example: "La Réunion" -> "la reunion"
 */
const normalizeForSearch = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Build country data from ISO code
 */
const buildCountryData = (code: CountryCode): CountryData => {
  const dialCode = `+${getCountryCallingCode(code)}`;
  const name = getCountryName(code);
  const flag = getFlagEmoji(code);

  // Build search terms: name (with and without accents), code, dial code (with and without +)
  const searchTerms = [
    normalizeForSearch(name),
    name.toLowerCase(),
    code.toLowerCase(),
    dialCode,
    dialCode.slice(1), // Without +
  ].join(' ');

  return {
    code,
    name,
    dialCode,
    flag,
    searchTerms,
  };
};

/**
 * Get all countries with France first, then alphabetically sorted by French name
 * Includes all French overseas territories (La Réunion, Mayotte, Guadeloupe, etc.)
 */
const getCountryData = (): CountryData[] => {
  const allCountries = getCountries();
  const countries: CountryData[] = [];

  // France first
  countries.push(buildCountryData('FR'));

  // All other countries sorted alphabetically by French name
  const otherCountries = allCountries
    .filter((code) => code !== 'FR')
    .map(buildCountryData)
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  return [...countries, ...otherCountries];
};

// Singleton cache to avoid recalculating on every render
let cachedCountryData: CountryData[] | null = null;

/**
 * Get all countries (cached)
 * Returns ~240 countries including all French overseas territories
 */
export const getAllCountries = (): CountryData[] => {
  if (!cachedCountryData) {
    cachedCountryData = getCountryData();
  }
  return cachedCountryData;
};

/**
 * Find a country by its ISO code
 */
export const getCountryByCode = (code: CountryCode): CountryData | undefined => {
  return getAllCountries().find((c) => c.code === code);
};

/**
 * Priority country count (just France)
 * Used to render a separator after priority countries
 */
export const PRIORITY_COUNTRY_COUNT = 1;
